import React from "react";
import { Link } from "react-router-dom";
import { Star, Heart, MapPin } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { API_BASE } from "@/lib/api";

const resolveMpUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  const base = API_BASE.replace(/\/api$/, "");
  return base + u;
};

/**
 * VenueCard — primary discovery card.
 * Used in the home Recommended / New / Trending carousels.
 */
export default function VenueCard({ l, compact = false }) {
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const name = (l?.name && (l.name[isAr ? "ar" : "en"] || l.name.en || l.name.ar)) || (isAr ? "صالون" : "Salon");
  const cover = l?.media?.cover || l?.media?.gallery?.[0] || null;
  const rating = l?.rating?.avg || 0;
  const reviewCount = l?.rating?.count || 0;
  const branch = l?.branches?.[0];
  const cityName = branch?.city_name || l?.area_label || "";
  const cat = (l?.categories || [])[0];
  const priceMin = l?.price_range?.min;
  const priceMax = l?.price_range?.max;
  const currency = l?.price_range?.currency || "QAR";

  return (
    <Link
      to={`/m/s/${l.slug}`}
      className="mp-card group block"
      data-testid={`mp-venue-card-${l.slug}`}
    >
      <div className="relative aspect-[4/3] bg-[var(--mp-cream)] overflow-hidden">
        {cover ? (
          <img src={resolveMpUrl(cover)} alt={name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[var(--mp-ink-soft)] text-sm">{isAr ? "بدون صورة" : "No image"}</div>
        )}
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          className="absolute top-3 end-3 w-9 h-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center shadow-sm hover:scale-110 transition"
          aria-label="favorite"
        >
          <Heart size={16} className="text-[var(--mp-ink)]" />
        </button>
        {l?.featured_until && new Date(l.featured_until) > new Date() && (
          <span className="absolute top-3 start-3 bg-[var(--mp-black)] text-white px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide">
            {isAr ? "مميّز" : "Featured"}
          </span>
        )}
      </div>

      <div className={`p-4 ${compact ? "space-y-1" : "space-y-1.5"}`}>
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-bold text-[15px] leading-snug text-[var(--mp-black)] line-clamp-1">{name}</h3>
        </div>

        <div className="mp-stars text-xs">
          <Star size={13} fill="currentColor" stroke="none" />
          <span className="mp-stars-text text-[13px]">{rating.toFixed(1)}</span>
          <span className="text-[var(--mp-ink-muted)] font-normal">({reviewCount})</span>
        </div>

        {cityName && (
          <div className="flex items-center gap-1 text-[12px] text-[var(--mp-ink-muted)]">
            <MapPin size={12} />
            <span className="truncate">{cityName}</span>
          </div>
        )}

        {!compact && (cat || (priceMin && priceMax)) && (
          <div className="flex items-center justify-between gap-2 pt-1">
            {cat && <span className="text-[11px] uppercase tracking-wider text-[var(--mp-ink-muted)]">{cat}</span>}
            {priceMin > 0 && priceMax > 0 && (
              <span className="text-[12px] font-semibold text-[var(--mp-black)]">
                {priceMin === priceMax ? `${priceMin}` : `${priceMin}–${priceMax}`} {currency}
              </span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
}
